import { AppBar, Toolbar, Typography, Button, Menu, MenuItem, Stack, IconButton, Portal } from '@mui/material';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useLayoutContext } from '../LayoutContext';

import useThemeBreakpoint from 'modules/common/useThemeBreakpoint';
import CollectiveIcon from 'modules/common/components/Icons/CollectiveIcon';
import HamburgerMenu2Icon from 'modules/common/components/Icons/HamburgerMenu2Icon';
import MemberAvatar from 'modules/common/components/Layout/common/MemberAvatar';
import IMC from 'modules/IMC';
import { useMember } from 'modules/common/queries/member';
import useAuthHandlers from 'modules/Auth/AuthProvider/useAuthHandlers';
import { IMPERSONATION_TRANSITION_PATH } from 'modules/Auth/AuthProvider/constants';
import useAuthSelectors from 'modules/Auth/AuthProvider/useAuthSelector';
import { useImpactAdvocateEnabled } from 'modules/common/hooks/useImpactAdvocateEnabled';

const Header: React.FC = () => {
    const { pageTitle, setIsSidebarHidden, isHeaderInBlendMode } = useLayoutContext();
    const breakpoint = useThemeBreakpoint();
    const { data: member } = useMember();
    const { isImpersonating } = useAuthSelectors('isImpersonating');

    const { logout, impersonateEnd } = useAuthHandlers();
    const history = useHistory();
    const isImpactAdvocateEnabled = useImpactAdvocateEnabled();

    // For the "Company" dropdown
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isUserMenuOpen = Boolean(anchorEl);

    const handleCompanyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSettings = () => {
        history.push('/settings');
        handleClose();
    };

    const handleReferrals = () => {
        history.push('/referrals');
        handleClose();
    };

    const handleLogout = () => {
        logout(async () => {
            handleClose();
            history.push('/login');
        });
    };

    const stopImpersonating = async () => {
        history.push(IMPERSONATION_TRANSITION_PATH);
        await impersonateEnd();
    };
    const textColor = isImpersonating ? 'white' : 'cement.700';
    const titleVariant = breakpoint === 'small' ? 'h5' : 'h4';

    return (
        <AppBar
            position="sticky"
            color="inherit"
            elevation={0}
            id="app-bar-header"
            sx={{
                borderBottom: 'none',
                transition:
                    'background-color 0.5s ease, border-bottom-left-radius 0.5s ease, border-bottom-right-radius 0.5s ease',
                ...(isHeaderInBlendMode && {
                    backgroundColor: 'cement.200',
                    borderBottomLeftRadius: '0px',
                    borderBottomRightRadius: '0px',
                }),
                ...(isImpersonating && { bgcolor: 'red.700', color: 'white' }),
                maxWidth: {
                    xs: '100dvw',
                    xl: '1800px',
                }, // for 2k resolutions and up
            }}>
            <Toolbar
                sx={{
                    minHeight: 48,
                    display: 'flex',
                    justifyContent: 'space-between',
                    px: { xs: 1.5 },
                }}>
                <Stack direction="row" alignItems="center" gap={2}>
                    {breakpoint === 'small' && <CollectiveIcon sx={{ fontSize: '2.75rem' }} />}
                    <Typography
                        variant={titleVariant}
                        aria-label="page title"
                        sx={{ color: textColor, flexGrow: 1, opacity: pageTitle ? 1 : 0, transition: 'opacity 0.2s' }}>
                        {pageTitle}
                    </Typography>
                </Stack>
                <Stack direction="row" alignItems="center">
                    {breakpoint !== 'small' && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                // IMC coloring, can be tweaked after main layout is merged
                                '& .item button': {
                                    color: textColor,
                                    '&:hover': { backgroundColor: 'transparent', color: 'black' },
                                    paddingX: 1.5,
                                    marginX: 0,
                                },
                                ...(isImpersonating &&
                                    member?.hasPaymentIssue && {
                                        marginRight: '160px',
                                    }),
                            }}>
                            <IMC hasPaymentIssue={member?.hasPaymentIssue} includePortal={false} />
                            <IconButton
                                aria-label="User menu"
                                color="secondary"
                                size="medium"
                                sx={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid',
                                    borderColor: 'cement.200',
                                    height: '32px',
                                    padding: 0,
                                }}
                                onClick={handleCompanyClick}>
                                <MemberAvatar variant="medium" bgcolor="transparent" typography="subtitle2" />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={isUserMenuOpen}
                                onClose={handleClose}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            minWidth: 240,
                                            maxWidth: 280,
                                            marginTop: 1,
                                        },
                                        elevation: 5,
                                    },
                                }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                                <MenuItem onClick={handleSettings}>Settings</MenuItem>
                                {isImpactAdvocateEnabled && <MenuItem onClick={handleReferrals}>Refer & Earn</MenuItem>}
                                <MenuItem onClick={handleLogout}>Sign out</MenuItem>
                            </Menu>
                        </Stack>
                    )}
                    {isImpersonating && (
                        <Portal disablePortal={!member?.hasPaymentIssue}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={stopImpersonating}
                                sx={{
                                    ...(member?.hasPaymentIssue && {
                                        position: 'fixed',
                                        top: {
                                            xs: 4,
                                            md: 12,
                                        },
                                        right: { xs: 8, md: 16 },
                                        zIndex: ({ zIndex }) => zIndex.modal + 1,
                                    }),
                                    ...(!member?.hasPaymentIssue && {
                                        ml: { xs: 'auto', md: 2 },
                                        mr: { xs: 2, md: 0 },
                                    }),
                                }}>
                                Stop Impersonating
                            </Button>
                        </Portal>
                    )}
                    {breakpoint === 'small' && (
                        <IconButton
                            disableRipple
                            aria-label="open drawer"
                            edge="start"
                            onClick={() => setIsSidebarHidden(false)}>
                            <HamburgerMenu2Icon />
                        </IconButton>
                    )}
                </Stack>
            </Toolbar>
        </AppBar>
    );
};

export default Header;
